import { useState } from 'react'
import { useAppDispatch } from '@/store/store-hooks'
import postCreateAgentChat from '@/agent/conversations/fetch/post-create-agent-chat'
import { AgentConversationsActions } from '@/agent/conversations/store/slice'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackMedChronEvent } from '@/analytics/utils/medchron-analytics'
import { postInitConversationWithMedChronAsset } from '../fetch/post-init-conversation-with-med-chron-asset'

interface UseStartMedChronAssetChatReturn {
  isLoading: boolean
  startChat: () => Promise<void>
}

type UseStartMedChronAssetChatProps = {
  assetId: string
  matterId: string
}

/**
 * Starts an assistant conversation grounded in the matter's living Medical Chronology:
 * creates the conversation, initializes it with the chronology asset, and opens it.
 */
export function useStartMedChronAssetChat(props: UseStartMedChronAssetChatProps): UseStartMedChronAssetChatReturn {
  const { assetId, matterId } = props
  const dispatch = useAppDispatch()
  const [isLoading, setIsLoading] = useState(false)

  const startChat = async () => {
    if (isLoading) return
    setIsLoading(true)
    trackMedChronEvent(AnalyticsEvent.MedChronAssetChatClicked, { medChronAssetId: assetId, matterId })

    try {
      const conversation = await postCreateAgentChat(matterId)
      await postInitConversationWithMedChronAsset(conversation.id, assetId)
      dispatch(AgentConversationsActions.upsertConversationFromRead(conversation))

      const conversationPath = buildConversationPath(matterId, conversation.id)
      const win = window.open(conversationPath, '_blank', 'noopener,noreferrer')
      if (win) win.opener = null
    } catch (error) {
      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'Failed to start chat. Please try again.',
          durationMs: kToastDurationMediumMs,
        })
      )
      console.error('Error creating medical chronology chat:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return { isLoading, startChat }
}
